import { XIcon } from 'lucide-react';
import { Dialog as DialogPrimitive } from 'radix-ui';
import type * as React from 'react';
import { useRef } from 'react';
import { Button } from '@/components/ui/button';
import { useDragDismiss } from '@/lib/use-drag-dismiss';
import { cn } from '@/lib/utils';

function Dialog({ ...props }: React.ComponentProps<typeof DialogPrimitive.Root>) {
  return <DialogPrimitive.Root data-slot="dialog" {...props} />;
}

function DialogTrigger({ ...props }: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
}

function DialogPortal({ ...props }: React.ComponentProps<typeof DialogPrimitive.Portal>) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />;
}

function DialogClose({ ...props }: React.ComponentProps<typeof DialogPrimitive.Close>) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />;
}

function DialogOverlay({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Overlay>) {
  return (
    <DialogPrimitive.Overlay
      data-slot="dialog-overlay"
      className={cn(
        'fixed inset-0 z-50 bg-black/50 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0',
        className,
      )}
      {...props}
    />
  );
}

function DialogContent({
  className,
  children,
  showCloseButton = true,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & {
  showCloseButton?: boolean;
}) {
  const closeRef = useRef<HTMLButtonElement>(null);

  // The pull-down gesture, shared with the vitals pin so a sheet and a panel
  // that look alike to a thumb behave alike. Radix owns the open state, so a
  // completed pull goes through its Close rather than any local flag; the sheet
  // is found from the handle by `closest()` because `props` may already carry a
  // `ref` for the content and this way we never fight it.
  const dragHandlers = useDragDismiss({
    onDismiss: () => closeRef.current?.click(),
    sheetOf: (handle) => handle.closest<HTMLElement>('[data-slot="dialog-content"]'),
  });

  return (
    <DialogPortal data-slot="dialog-portal">
      <DialogOverlay />
      <DialogPrimitive.Content
        data-slot="dialog-content"
        className={cn(
          // `minmax(0,1fr)` for the one column, not the implicit `auto`: an
          // auto track's minimum is its widest child's min-content, so one
          // wide row (a chip strip, a long code) silently widens *everything*
          // in the dialog past the sheet edge. Zero-minimum makes children
          // shrink and wrap instead.
          'fixed z-50 grid w-full grid-cols-[minmax(0,1fr)] gap-4 border bg-background p-6 shadow-lg duration-200 outline-none',
          'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0',
          // Below `sm` a dialog is a bottom sheet: full-width, anchored to the
          // bottom edge, sliding up, height-capped with its own scrolling.
          // `pt-8` reserves the strip the grab handle floats in (it wins over a
          // dialog's own `p-0` — media-query utilities are later in the
          // stylesheet), `overflow-x-hidden` is the global guard that no
          // dialog's content can make the sheet scroll sideways, and 300 ms
          // eases the slide the way a sheet moves rather than a popup.
          //
          // The bottom edge is the keyboard's top edge, not the page's: on
          // WebKit a software keyboard shrinks only the visual viewport, so
          // `bottom: 0` would leave the footer behind it. `lib/software-keyboard.ts`
          // publishes the pixels hidden below the visible box, and the cap
          // comes down to match — a sheet that keeps a tenth of the screen
          // clear above a keyboard already holding half of it is unusable.
          // Both fall back to today's values wherever nothing is published.
          //
          // `pb-0`, not a bottom padding: the room under the footer is the
          // trailing spacer below `children`. See the note on it.
          'max-sm:inset-x-0 max-sm:bottom-(--keyboard-inset,0px) max-sm:top-auto max-sm:max-h-[min(90dvh,var(--visual-viewport-height,100dvh))] max-sm:translate-x-0 max-sm:translate-y-0 max-sm:overflow-y-auto max-sm:overflow-x-hidden max-sm:rounded-t-xl max-sm:rounded-b-none max-sm:pt-8 max-sm:pb-0 max-sm:duration-300 max-sm:data-[state=closed]:slide-out-to-bottom max-sm:data-[state=open]:slide-in-from-bottom',
          // Centred in what is visible rather than in what is laid out: an
          // iPad's docked keyboard covers the lower half of a box centred in
          // the layout viewport, which is the half the footer is in, and the
          // cap lets a box taller than that scroll inside itself.
          'sm:top-[calc(var(--visual-viewport-top,0px)+var(--visual-viewport-height,100dvh)/2)] sm:left-[50%] sm:max-h-[calc(var(--visual-viewport-height,100dvh)-2rem)] sm:max-w-lg sm:translate-x-[-50%] sm:translate-y-[-50%] sm:overflow-y-auto sm:rounded-lg sm:data-[state=closed]:zoom-out-95 sm:data-[state=open]:zoom-in-95',
          // A tablet is wide enough for the centred box above and still has only
          // a thumb, so the handle stays on there too and needs its strip
          // reserved at every width, not just below `sm`.
          'pointer-coarse:pt-8',
          className,
        )}
        {...props}
      >
        {/* The grab handle: the sheet affordance, and the drag-to-dismiss
            surface. Only the handle drags — putting the gesture on the whole
            sheet would fight every scrollable list inside one. `touch-none`
            keeps the browser from treating the drag as a scroll. Decorative to
            the accessibility tree: Escape, the X and the overlay are the
            accessible ways out, this one is for thumbs.

            It goes away for a mouse, not at a width. A tablet crosses `sm` and
            is still a device with no hover and no Escape key, so dropping the
            gesture there left it the small X and the overlay. `pointer-fine`
            hides the handle where a cursor makes it pointless and keeps it
            everywhere a thumb is. The pull writes `transform` on the sheet,
            which the centred box at `sm` survives: Tailwind centres it with the
            `translate` property, a separate one. */}
        <div
          aria-hidden="true"
          data-slot="dialog-handle"
          className="absolute inset-x-0 top-0 flex h-7 touch-none items-start justify-center pt-2 pointer-fine:sm:hidden"
          {...dragHandlers}
        >
          <div className="h-1.5 w-10 rounded-full bg-muted-foreground/25" />
        </div>
        {/* The programmatic way out for the gesture above: Radix owns the open
            state, and its Close is the one lever that always agrees with it.
            Display-none and aria-hidden — no accessible surface, only
            `.click()`. */}
        <DialogPrimitive.Close ref={closeRef} aria-hidden="true" tabIndex={-1} className="hidden" />
        {children}
        {/* The breathing room under a sheet's footer, and the gesture bar's
            inset where a phone has one. It was bottom padding on the sheet,
            and on a phone the footer's button sat hard on the screen edge: the
            sheet is a scroll container, and the padding at the end of one is
            the part of a box engines have not agreed to count in the
            scrollable overflow, so WebKit drew the content to the edge and
            the padding never showed. A block inside the content is counted by
            all of them. `-mt-4` cancels the grid gap above it so the space is
            the spacer's height and nothing more; a dialog that sets its own
            gap sets its own spacer arithmetic. Hidden from `sm` up, where the
            box is centred and its padding is not at the end of anything that
            scrolls the whole sheet. */}
        <div
          aria-hidden="true"
          data-slot="dialog-sheet-inset"
          className="-mt-4 h-[max(1.5rem,env(safe-area-inset-bottom))] sm:hidden"
        />
        {showCloseButton && (
          <DialogPrimitive.Close
            data-slot="dialog-close"
            className="absolute top-4 right-4 rounded-xs opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4"
          >
            <XIcon />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
}

function DialogHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="dialog-header"
      className={cn('flex flex-col gap-2 text-center sm:text-left', className)}
      {...props}
    />
  );
}

function DialogFooter({
  className,
  showCloseButton = false,
  children,
  ...props
}: React.ComponentProps<'div'> & {
  showCloseButton?: boolean;
}) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn('flex flex-col-reverse gap-2 sm:flex-row sm:justify-end', className)}
      {...props}
    >
      {children}
      {showCloseButton && (
        <DialogPrimitive.Close asChild>
          <Button variant="outline">Close</Button>
        </DialogPrimitive.Close>
      )}
    </div>
  );
}

function DialogTitle({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn('text-lg leading-none font-semibold', className)}
      {...props}
    />
  );
}

function DialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn('text-sm text-muted-foreground', className)}
      {...props}
    />
  );
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};
